import React, { useState } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { Sparkles, CheckCircle, ArrowRight, Star } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FLYER_IMAGE = 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/btsm243j_Luxury%20Flyer.png';

const AIFlyersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-service/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: 'ai_flyer', origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error('No checkout URL');
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const features = [
    'Custom luxury flyer designed from your photo',
    'Professional layout with your branding',
    'Optional payment QR code integration',
    'High-resolution digital file for print & social',
    'Fast 24-48 hour turnaround',
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/10 to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-600/30 text-amber-400 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Sparkles className="w-4 h-4" /> AI-Powered Design
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="flyer-hero-title">
                AI Luxury<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500">Flyer Design</span>
              </h1>
              <p className="text-gray-400 text-lg mb-8 max-w-lg mx-auto lg:mx-0">
                Turn your photo into a stunning, professional flyer. Perfect for events, promotions, business launches, and personal branding.
              </p>

              <ul className="space-y-3 mb-10 text-left max-w-md mx-auto lg:mx-0">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Pricing + CTA */}
              <div className="bg-gray-900/60 border border-amber-800/30 rounded-2xl p-8 max-w-md mx-auto lg:mx-0">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-white">$29</span>
                  <span className="text-gray-500 text-lg">one-time</span>
                </div>
                <p className="text-amber-400 font-semibold text-sm mb-6">Includes custom design + unlimited revisions</p>
                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold py-4 text-lg rounded-full"
                  data-testid="flyer-buy-btn"
                >
                  {loading ? 'Redirecting to checkout...' : 'Get Your Flyer — $29'}
                  {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>
              </div>
            </div>

            {/* Right - Flyer Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-600/20 to-yellow-600/20 rounded-3xl blur-2xl" />
                <img
                  src={FLYER_IMAGE}
                  alt="AI Luxury Flyer Example"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl border border-amber-800/20"
                  data-testid="flyer-sample-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t border-gray-800/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Purchase', desc: 'Complete your $29 payment securely via Stripe.' },
              { step: '02', title: 'Submit Details', desc: 'Upload your photo and tell us about your event or brand.' },
              { step: '03', title: 'Receive Your Flyer', desc: 'Get your custom flyer within 24-48 hours.' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-amber-600/20 border border-amber-600/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-amber-400 font-bold text-lg">{s.step}</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 border-t border-gray-800/50">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
          </div>
          <p className="text-gray-300 text-lg italic mb-4">"My flyer looked like I hired a whole design team. People kept asking who made it!"</p>
          <p className="text-amber-400 font-semibold">— Satisfied Client</p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 border-t border-gray-800/50">
        <div className="container mx-auto px-4 text-center max-w-xl">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Stand Out?</h2>
          <p className="text-gray-400 mb-8">Get a professional flyer designed from your photo in under 48 hours.</p>
          <Button
            onClick={handleCheckout}
            disabled={loading}
            className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold px-10 py-4 text-lg rounded-full"
            data-testid="flyer-buy-btn-bottom"
          >
            {loading ? 'Redirecting...' : 'Get Your Flyer — $29'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <div className="py-8 border-t border-gray-800/50 text-center">
        <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBook AI Luxury Flyer Design</p>
        <p className="text-gray-600 text-xs mt-1">Powered by eBlackBookListings</p>
      </div>
    </div>
  );
};

export default AIFlyersPage;
